import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPresignedUploadUrl, uploadFileToS3WithProgress, getGuestPhotos, deleteGuestMedia, PhotoItem } from '../services/api';
import ScrollReveal from './animations/ScrollReveal';

interface Props {
  guestCode: string;
}

// Upload item interface for tracking multiple uploads
interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

// Constants
const MAX_FILE_SIZE = 4.5 * 1024 * 1024 * 1024; // 4.5GB in bytes
const MAX_CONCURRENT_UPLOADS = 3;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mov', 'video/avi'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];

// Generate unique ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const PhotoGallery: React.FC<Props> = ({ guestCode }) => {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Extract filename from S3 key (removes the code/ prefix)
  const extractFilename = (key: string): string => {
    const parts = key.split('/');
    return parts.length > 1 ? parts.slice(1).join('/') : key;
  };

  // Handle delete media
  const handleDeleteMedia = async (photo: PhotoItem) => {
    const filename = extractFilename(photo.key);

    // Confirm deletion
    const confirmed = window.confirm('¿Estás seguro de que quieres eliminar este archivo? Esta acción no se puede deshacer.');
    if (!confirmed) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteGuestMedia(guestCode, filename);
      // Close lightbox and refresh
      setSelectedPhoto(null);
      await loadPhotos();
    } catch (error) {
      console.error('Error deleting media:', error);
      setDeleteError(error instanceof Error ? error.message : 'Error al eliminar el archivo');
    } finally {
      setIsDeleting(false);
    }
  };

  // Load photos from the server
  const loadPhotos = useCallback(async () => {
    try {
      setIsLoading(true);
      const guestPhotos = await getGuestPhotos(guestCode);
      setPhotos(guestPhotos);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [guestCode]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  // Helper to check if a file/key is a video
  const isVideo = (keyOrType: string): boolean => {
    const videoExtensions = ['.mp4', '.mov', '.webm', '.avi'];
    const lower = keyOrType.toLowerCase();
    return videoExtensions.some(ext => lower.endsWith(ext)) || ALLOWED_VIDEO_TYPES.includes(lower);
  };

  // Validate a single file
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de archivo no permitido. Solo fotos y videos.',
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'El archivo es demasiado grande para subirlo desde el navegador (Máx 4.5GB).',
      };
    }

    return { valid: true };
  };

  // Upload a single file
  const uploadSingleFile = async (item: UploadItem): Promise<void> => {
    // Update status to uploading
    setUploadQueue(prev =>
      prev.map(u => (u.id === item.id ? { ...u, status: 'uploading' as const, progress: 0 } : u))
    );

    try {
      // Get presigned URL
      const { uploadUrl } = await getPresignedUploadUrl(guestCode, item.file);

      // Upload with progress tracking
      await uploadFileToS3WithProgress(uploadUrl, item.file, (progress) => {
        setUploadQueue(prev =>
          prev.map(u => (u.id === item.id ? { ...u, progress } : u))
        );
      });

      // Mark as completed
      setUploadQueue(prev =>
        prev.map(u => (u.id === item.id ? { ...u, status: 'completed' as const, progress: 100 } : u))
      );
    } catch (error) {
      // Mark as error
      setUploadQueue(prev =>
        prev.map(u =>
          u.id === item.id
            ? {
                ...u,
                status: 'error' as const,
                error: error instanceof Error ? error.message : 'Error desconocido',
              }
            : u
        )
      );
    }
  };

  // Process upload queue with concurrency limit
  const processUploadQueue = useCallback(async (items: UploadItem[]) => {
    const pendingItems = items.filter(item => item.status === 'pending');

    // Process in batches of MAX_CONCURRENT_UPLOADS
    for (let i = 0; i < pendingItems.length; i += MAX_CONCURRENT_UPLOADS) {
      const batch = pendingItems.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map(item => uploadSingleFile(item)));
    }

    // Refresh gallery after all uploads complete
    await loadPhotos();
  }, [guestCode, loadPhotos]);

  // Handle files from input or drop
  const handleFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newItems: UploadItem[] = [];

    fileArray.forEach(file => {
      const validation = validateFile(file);

      if (validation.valid) {
        newItems.push({
          id: generateId(),
          file,
          progress: 0,
          status: 'pending',
        });
      } else {
        // Add as error item
        newItems.push({
          id: generateId(),
          file,
          progress: 0,
          status: 'error',
          error: validation.error,
        });
      }
    });

    if (newItems.length > 0) {
      setUploadQueue(prev => [...prev, ...newItems]);

      // Start processing pending items
      const validItems = newItems.filter(item => item.status === 'pending');
      if (validItems.length > 0) {
        processUploadQueue([...uploadQueue, ...newItems]);
      }
    }
  }, [uploadQueue, processUploadQueue]);

  // File input change handler
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  // Remove item from queue
  const removeFromQueue = (id: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  // Clear completed/error items
  const clearFinishedItems = () => {
    setUploadQueue(prev => prev.filter(item => item.status === 'pending' || item.status === 'uploading'));
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    });
  };

  // Get status icon for upload item
  const getStatusIcon = (status: UploadItem['status']) => {
    switch (status) {
      case 'pending':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        );
      case 'uploading':
        return (
          <div className="upload-spinner-small" />
        );
      case 'completed':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        );
      case 'error':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
    }
  };

  const hasActiveUploads = uploadQueue.some(item => item.status === 'pending' || item.status === 'uploading');
  const hasFinishedItems = uploadQueue.some(item => item.status === 'completed' || item.status === 'error');

  return (
    <section className="photo-gallery-section">
      {/* Background */}
      <div className="photo-gallery-background">
        <div className="photo-gallery-background-overlay"></div>
      </div>

      {/* Header */}
      <ScrollReveal direction="up" delay={0.2}>
        <div className="photo-gallery-header">
          <h1 className="photo-gallery-subtitle">GALERIA</h1>
          <h2 className="photo-gallery-title-script">Comparte tus Recuerdos</h2>
          <p className="photo-gallery-description">
            Sube tus fotos y videos favoritos de la celebración
          </p>
        </div>
      </ScrollReveal>

      {/* Content */}
      <ScrollReveal direction="up" delay={0.4}>
        <div className="photo-gallery-content">
          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className={`photo-dropzone ${isDragOver ? 'drag-over' : ''} ${hasActiveUploads ? 'has-uploads' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !hasActiveUploads && fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
              style={{ display: 'none' }}
              multiple
            />

            <div className="dropzone-content">
              <div className="dropzone-icon">
                {isDragOver ? (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                ) : (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                )}
              </div>
              <p className="dropzone-text">
                {isDragOver ? (
                  '¡Suelta aquí tus archivos!'
                ) : (
                  <>
                    <span className="dropzone-cta">Arrastra y suelta</span> tus fotos y videos aquí
                    <br />
                    <span className="dropzone-hint">o haz clic para seleccionar</span>
                  </>
                )}
              </p>
              <p className="dropzone-limits">
                Máx. 4.5GB por archivo • Fotos y videos
              </p>
            </div>
          </div>

          {/* Upload Queue */}
          <AnimatePresence>
            {uploadQueue.length > 0 && (
              <motion.div
                className="upload-queue"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <div className="upload-queue-header">
                  <span className="upload-queue-title">
                    {hasActiveUploads
                      ? `Subiendo ${uploadQueue.filter(i => i.status === 'uploading').length} de ${uploadQueue.length} archivos...`
                      : `${uploadQueue.filter(i => i.status === 'completed').length} archivos subidos`}
                  </span>
                  {hasFinishedItems && !hasActiveUploads && (
                    <button className="upload-queue-clear" onClick={clearFinishedItems}>
                      Limpiar lista
                    </button>
                  )}
                </div>

                <div className="upload-queue-list">
                  {uploadQueue.map(item => (
                    <motion.div
                      key={item.id}
                      className={`upload-queue-item status-${item.status}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <div className="upload-item-icon">
                        {isVideo(item.file.type) ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="23 7 16 12 23 17 23 7" />
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        )}
                      </div>

                      <div className="upload-item-info">
                        <span className="upload-item-name" title={item.file.name}>
                          {item.file.name.length > 30
                            ? `${item.file.name.substring(0, 27)}...`
                            : item.file.name}
                        </span>
                        <span className="upload-item-size">{formatFileSize(item.file.size)}</span>
                        {item.error && (
                          <span className="upload-item-error">{item.error}</span>
                        )}
                      </div>

                      {item.status === 'uploading' && (
                        <div className="upload-item-progress">
                          <div
                            className="upload-item-progress-bar"
                            style={{ width: `${item.progress}%` }}
                          />
                          <span className="upload-item-progress-text">{item.progress}%</span>
                        </div>
                      )}

                      <div className="upload-item-status">
                        {getStatusIcon(item.status)}
                      </div>

                      {(item.status === 'completed' || item.status === 'error') && (
                        <button
                          className="upload-item-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromQueue(item.id);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Photo Grid */}
          <div className="photo-grid-container">
            {isLoading ? (
              <div className="photo-loading">
                <div className="photo-loading-spinner"></div>
                <p>Cargando recuerdos...</p>
              </div>
            ) : photos.length === 0 ? (
              <div className="photo-empty">
                <span className="photo-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </span>
                <p>Aún no hay recuerdos</p>
                <span className="photo-empty-hint">¡Sé el primero en compartir una foto o video!</span>
              </div>
            ) : (
              <div className="photo-grid">
                {photos.map((photo, index) => (
                  <motion.div
                    key={photo.key}
                    className={`photo-grid-item ${isVideo(photo.key) ? 'video-item' : ''}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    {isVideo(photo.key) ? (
                      <>
                        <video src={photo.url} preload="metadata" muted playsInline />
                        <div className="video-play-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        </div>
                      </>
                    ) : (
                      <img src={photo.url} alt={`Recuerdo ${index + 1}`} loading="lazy" />
                    )}
                    <div className="photo-overlay">
                      <span className="photo-date">{formatDate(photo.lastModified)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ScrollReveal>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            className="photo-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPhoto(null)}
          >
            <motion.div
              className="photo-lightbox-content"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                className="photo-lightbox-close"
                onClick={() => setSelectedPhoto(null)}
                title="Cerrar"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              {/* Delete button */}
              <button
                className="photo-lightbox-delete"
                onClick={() => handleDeleteMedia(selectedPhoto)}
                disabled={isDeleting}
                title="Eliminar"
              >
                {isDeleting ? (
                  <div className="upload-spinner-small" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                )}
              </button>

              {/* Delete error message */}
              {deleteError && (
                <div className="photo-lightbox-error">
                  {deleteError}
                </div>
              )}

              {isVideo(selectedPhoto.key) ? (
                <video
                  src={selectedPhoto.url}
                  controls
                  autoPlay
                  playsInline
                  style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '0.5rem' }}
                />
              ) : (
                <img src={selectedPhoto.url} alt="Foto ampliada" />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom spacing */}
      <div className="photo-gallery-bottom"></div>
    </section>
  );
};

export default PhotoGallery;
